/**
 * The one hook a widget container uses to get its data.
 *
 *   const { status, data, retry } = useWidgetData('mention-volume-over-time');
 *
 * It reads the active source, the applied (debounced) filters and the page's
 * filters bar type, builds the canonical query, and maps RTK Query state to a
 * card status. `currentData` (not `data`) is used on purpose: while filters
 * change, the card shows its loading state instead of stale numbers — the
 * prototype's behaviour.
 */
import { useMemo } from 'react';

import type { WidgetData, WidgetId, WidgetParams } from '@/data-sources/contract';
import { isApiError, type ApiError } from '@/lib/errors';

import { useAppliedFilters } from '../data/AppliedFiltersContext';
import { useDataSource } from '../data/DataSourceContext';
import { usePageMeta } from '../data/PageContext';
import { canonicalQuery } from '../filters/canonicalQuery';
import { useReportScope, withReportScope } from '../filters/useReportScope';
import { widgetsApi } from './widgets.api';

export type WidgetStatus = 'loading' | 'ready' | 'error' | 'notConnected';

export type WidgetResult<W extends WidgetId> = {
  status: WidgetStatus;
  data: WidgetData<W> | undefined;
  error: ApiError | undefined;
  retry: () => void;
};

type BaseOptions = {
  skip?: boolean;
  /**
   * Leave the date range out of the query (and the cache key). For widgets
   * that draw a fixed window regardless of the picker, e.g. the activity
   * heatmap's last 8 weeks — they then do not reload when the dates change.
   */
  omitDates?: boolean;
};

type Options<W extends WidgetId> =
  WidgetParams<W> extends undefined
    ? BaseOptions & { params?: undefined }
    : BaseOptions & { params: WidgetParams<W> };

export function useWidgetData<W extends WidgetId>(
  id: W,
  ...[options]: WidgetParams<W> extends undefined ? [Options<W>?] : [Options<W>]
): WidgetResult<W> {
  const { source } = useDataSource();
  const filters = useAppliedFilters();
  const { filtersType } = usePageMeta();
  // On a custom report, the report scopes its widgets (campaign posts).
  const dashboardId = useReportScope();
  const omitDates = options?.omitDates ?? false;
  const query = useMemo(() => {
    const q = canonicalQuery(filters, filtersType);
    const scoped = withReportScope(q, dashboardId);
    return omitDates ? { ...scoped, range: { start: null, end: null }, compare: null } : scoped;
  }, [filters, filtersType, omitDates, dashboardId]);

  const result = widgetsApi.useWidgetQuery(
    { source, params: { id, query, params: options?.params } },
    { skip: options?.skip, refetchOnFocus: source === 'live' },
  );

  const error = isApiError(result.error) ? result.error : undefined;
  const status: WidgetStatus = result.isError
    ? error?.kind === 'notConnected'
      ? 'notConnected'
      : 'error'
    : result.currentData === undefined
      ? 'loading'
      : 'ready';

  return {
    status,
    data: result.currentData as WidgetData<W> | undefined,
    error,
    retry: result.refetch,
  };
}
